<template>
	<div>
		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{snackbar.message}}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false">
				Close
			</v-btn>
		</v-snackbar>
		<v-form>
			<v-container grid-list-xl fluid>
				<v-alert v-if="!complete" class="text-xs-left" outline :value="true" type="info" icon="person"> You are filling out this Performance Review as {{ submitterName }} </v-alert>

				<v-alert v-if="complete" class="text-xs-left" outline :value="true" type="success" icon="check"> Completed <AppDate :date="submit.completedDateUTC" /> by {{ submitterName }} </v-alert>

				<v-layout row wrap>
					<v-flex lg6>
						<v-card>
							<v-toolbar flat dense cad dark class="primary">
								<v-toolbar-title class="subheading">Performance Review ({{ version }} Version)</v-toolbar-title>
								<v-spacer></v-spacer>

								<v-btn icon flat @click="print">
									<v-badge color="red" overlap>
										<v-icon medium>print</v-icon>
									</v-badge>
								</v-btn>

								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn v-if="isDefaultReviewType && isReviewer" icon flat v-on="on" @click="reviewerHelpDialog = !reviewerHelpDialog">
											<v-badge color="red" overlap>
												<v-icon medium>mdi-information-variant</v-icon>
											</v-badge>
										</v-btn>
									</template>
									<span>Tips for the Reviewer</span>
								</v-tooltip>

								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn v-if="isDefaultReviewType && isEmployee" icon flat v-on="on" @click="employeeHelpDialog = !employeeHelpDialog">
											<v-badge color="red" overlap>
												<v-icon medium>mdi-information-variant</v-icon>
											</v-badge>
										</v-btn>
									</template>
									<span>Tips for the Employee</span>
								</v-tooltip>
							</v-toolbar>
							<v-divider></v-divider>

							<v-list two-line>
								<v-list-tile inactive>
									<v-list-tile-action>
										<v-avatar size="36px">
											<img :src="getAvatar(performanceReview.employee.id)" alt="avatar" class="avatar-img" />
										</v-avatar>
									</v-list-tile-action>
									<v-list-tile-content>
										<v-list-tile-title>{{ performanceReview.employee.fullName }}</v-list-tile-title>
										<v-list-tile-sub-title>Name</v-list-tile-sub-title>
									</v-list-tile-content>
								</v-list-tile>

								<v-divider inset></v-divider>

								<v-list-tile inactive>
									<v-list-tile-action>
										<v-avatar size="36px">
											<img :src="getAvatar(performanceReview.reviewer.id)" alt="avatar" class="avatar-img" />
										</v-avatar>
									</v-list-tile-action>
									<v-list-tile-content>
										<v-list-tile-title>{{ performanceReview.reviewer.fullName }}</v-list-tile-title>
										<v-list-tile-sub-title>Reviewer</v-list-tile-sub-title>
									</v-list-tile-content>
								</v-list-tile>
							</v-list>
						</v-card>

						<traffic-light-helper v-if="anyTrafficLightRankingItems" />
					</v-flex>

					<v-flex lg6>
						<v-card>
							<v-toolbar flat dense cad>
								<v-toolbar-title class="subheading">Position Summary</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-divider></v-divider>

							<v-list two-line>
								<v-list-tile inactive>
									<v-list-tile-action>
										<v-icon color="indigo">person</v-icon>
									</v-list-tile-action>
									<v-list-tile-content>
										<v-list-tile-title>{{ performanceReview.position !== null ? performanceReview.position.title : 'N/A' }}</v-list-tile-title>
										<v-list-tile-sub-title>Position Title</v-list-tile-sub-title>
									</v-list-tile-content>
								</v-list-tile>
							</v-list>
							<div class="pd-container">
								<h2>Position Purpose</h2>
								<p>{{ performanceReview.position !== null ? performanceReview.position.description : 'N/A' }}</p>
							</div>
						</v-card>
					</v-flex>

					<v-flex lg6 text-xs-left> </v-flex>
				</v-layout>

				<v-layout row wrap class="reflect">
					<v-flex>
						<v-card class="mb-4">
							<v-toolbar flat dense cad>
								<v-toolbar-title class="subheading">Reflect</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-divider></v-divider>

							<v-card-text class="">
								<div v-for="group in itemsSorted" :key="group.group" class="type">
									<div class="text-xs-left">
										<h2 style="margin-bottom: 20px; margin-top:10px;">
											{{ group.group }}
										</h2>
										<h5 v-if="group.type == '3'">
											This is the team members opportunity to provide qualified and specific feedback to their Manager
										</h5>
									</div>
									<div v-for="t in group.items" :key="t.id">
										<div class="text-xs-left">
											<h4 v-if="t.category !== 'N/A'" style="margin-top:30px; margin-bottom:20px; color: rgba(0,0,0,0.54);">
												{{ t.category }}
											</h4>
										</div>
										<div v-for="s in t.items" :key="s.id" class="item">
											<v-layout row wrap>
												<v-flex text-xs-left md7 xs12>
													<div>
														<div
															style="border-left-color: lightgray;
                    padding-left: 10px; padding-right: 10px;"
															:class="{ 'item-text': true, 'na-text': s.result == '-1', 'green-text': s.result == '2', 'amber-text': s.result == '1', 'red-text': s.result == '0' }"
														>
															<table>
																<tr>
																	<td style="width: 1px;">
																		<v-menu transition="scale-transition" lazy right absolute offset-y :class="{ 'disabled': !canEdit }">
																			<v-btn
																				v-if="s.item.rankType === 1"
																				slot="activator"
																				:class="{ 'item-btn': true, success: s.result == '2', warning: s.result == '1', error: s.result == '0', 'disabled': !canEdit }"
																				fab
																				small
																			>
																				<v-icon v-if="s.result != '-1'" style="font-size: 14px;">{{ getIcon(s.result) }}</v-icon>
																				<span v-if="s.result == '-1'" style="font-size:9px;">n/a</span>
																			</v-btn>

																			<div style="padding:5px; background-color: #fff" v-if="canEdit">
																				<v-btn :title="greenDescription" color="#4caf50" fab small dark style="height:30px; width: 30px;" @click="onRateItem(s.id, 2)"> </v-btn>
																				<v-btn :title="amberDescription" color="#ffc107" fab small dark style="height:30px; width: 30px;" @click="onRateItem(s.id, 1)"> </v-btn>
																				<v-btn :title="redDescription" color="#f44336" fab small dark style="height:30px; width: 30px;" @click="onRateItem(s.id, 0)"> </v-btn>
																				<v-btn :title="naDescription" small style="height:30px; width: 30px;" @click="onRateItem(s.id, -1)">N/A</v-btn>
																			</div>
																		</v-menu>
																	</td>

																	<td>{{ s.item.item }}</td>
																</tr>
															</table>
														</div>

														<div v-for="outcome in s.outcomes" :key="outcome.id" style="margin-left:20px;">
															<div
																style="border-left-color: lightgray; padding-left: 10px; padding-right: 10px;"
																:class="{ 'item-text': true, 'na-text': outcome.result == '-1', 'green-text': outcome.result == '2', 'amber-text': outcome.result == '1', 'red-text': outcome.result == '0' }"
															>
																<table>
																	<tr>
																		<td style="width: 1px;">
																			<v-menu transition="scale-transition" lazy right absolute offset-y :class="{ 'disabled': !canEdit }">
																				<v-btn slot="activator" :class="{ 'item-btn': true, success: outcome.result == '2', warning: outcome.result == '1', error: outcome.result == '0', 'disabled': !canEdit }" fab small>
																						<v-icon v-if="outcome.result != '-1'" style="font-size: 14px">{{ getIcon(outcome.result) }}</v-icon>
																						<span v-if="outcome.result == '-1'" style="font-size: 9px">n/a</span>
																				</v-btn>

																				<div style="padding:5px; background-color: #fff" v-if="canEdit">
																					<v-btn :title="greenDescription" color="#4caf50" fab small dark style="height:30px; width: 30px;" @click="onRateItemOutcome(s.id, outcome.id, 2)">
																					</v-btn>
																					<v-btn :title="amberDescription" color="#ffc107" fab small dark style="height:30px; width: 30px;" @click="onRateItemOutcome(s.id, outcome.id, 1)">
																					</v-btn>
																					<v-btn
																						:title="redDescription"
																						color="#f44336"
																						fab
																						small
																						dark
																						style="height:30px; width: 30px;"
																						@click="onRateItemOutcome(s.id, outcome.id, 0)"
																					>
																					</v-btn>
																					<v-btn :title="naDescription" small style="height:30px; width: 30px;" @click="onRateItemOutcome(s.id, outcome.id, -1)">N/A</v-btn>
																				</div>
																			</v-menu>
																		</td>

																		<td>{{ outcome.outcome.outcome }}</td>
																	</tr>
																</table>
															</div>
														</div>
													</div>
												</v-flex>

												<v-flex md5 xs12>
													<v-textarea v-model="s.comment" :readonly="!canEdit" rows="2" required box auto-grow label="Comments"></v-textarea>
												</v-flex>
											</v-layout>

											<v-divider style="margin-bottom:20px;"></v-divider>
										</div>
									</div>
								</div>

								<div v-if="version === 'Reviewer' && isDefaultReviewType">
									<h2 class="text-xs-left">Notes</h2>

									<h5 class="text-xs-left" style="margin-top:10px; margin-bottom: 10px;">
										* Please Note: the employee will be able to see these notes during the discussion phase of the review.
									</h5>

									<v-textarea auto-grow v-model="form.notes" :readonly="!canEdit" box label="Notes" rows="3"></v-textarea>
								</div>

								<div v-if="version === 'Employee' && isDefaultReviewType">
									<div class="form-group"></div>

									<h2 class="text-xs-left">Other Duties</h2>

									<h5 class="text-xs-left" style="margin-top:10px; margin-bottom: 10px;">
										Are there any other duties that you are currently doing that have not been listed?
									</h5>

									<v-textarea auto-grow v-model="form.otherDuties" :readonly="!canEdit" box label="List duties" rows="3"></v-textarea>

									<h2 class="text-xs-left">Do you have any concerns regarding your job?</h2>

									<v-textarea auto-grow v-model="form.concerns" :readonly="!canEdit" box label="Any concerns you wish to raise" rows="3"></v-textarea>
								</div>
							</v-card-text>
						</v-card>
					</v-flex>
				</v-layout>

				<v-footer class="noPrint" color="" app height="55">
					<v-spacer></v-spacer>

					<span class="mr-2" :class="{ 'items-remaining': !formComplete }">{{ itemsRemainingString }}</span>
					
					<span class="last-saved-date ml-1 mr-2" v-if="!complete && autoSave && lastSavedDate">(Last saved <AppDate :date="lastSavedDate" format="hh:mm a" prepend-tooltip="Last saved" show-date />)</span>			
					<v-switch v-if="!complete && canEdit" v-model="autoSave" label="Auto Save" class="mr-2" style="flex:none;"></v-switch>

					<footer-back-btn @click="cancel" />
					<v-btn v-if="!complete && !autoSave & canEdit" @click="submitForm(false, false)" color="primary" :disabled="isSaving" :loading="isSaving">Save <span slot="loader">Saving...</span></v-btn>
					<v-btn v-if="canEdit && formComplete" class="action-btn" :dark="!isSubmitting" @click="onSignOffClick" :disabled="isSubmitting" :loading="isSubmitting">{{saveAndSignOffText}}<span slot="loader">Saving...</span></v-btn>
				</v-footer>
			</v-container>
		</v-form>

		<v-dialog v-model="employeeHelpDialog" width="900">
			<v-card>
				<v-card-title class="headline grey lighten-2" primary-title>
					Tips to get the most out of the performance review process
				</v-card-title>

				<v-card-text class="text-xs-left">
					<ul>
						<li>Approach the review with an open mind, be honest and make adjustments during the discussion.</li>
						<li>Consider all the positive aspects of your performance as it’s easy to focus the discussion on what could be improved and overlook all the positive aspects.</li>
						<li>In what areas do you believe your performance has fallen short of expectations?</li>
						<li>Use concrete examples by noting specifics that demonstrate positive performance and areas in need of improvement.</li>
						<li>
							Focus your part of the review on facts or evidence of actual work performance which is linked to your PD. Your own rating needs to be based on the business standards required, not on where you ‘should’ be considering time
							and experience.
						</li>
						<li>Conclude the review by setting a S.M.A.R.T* goal for the learning and development outcomes you would like to achieve. *Specific, Measurable, Achievable, Realistic and Timely</li>
					</ul>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" flat @click="employeeHelpDialog = false">
						Close
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="reviewerHelpDialog" width="900">
			<v-card>
				<v-card-title class="headline grey lighten-2" primary-title>
					Tips to get the most out of the performance review process
				</v-card-title>

				<v-card-text class="text-xs-left">
					<ul>
						<li>Remember to reinforce all the positive aspects of performance as it’s easy to focus the discussion on what needs improving and overlook all the positive aspects.</li>

						<li>Review goals set from the previous year. To what degree were the expected results achieved? What steps can be taken to remove any barriers that interfere with the achievement of these goals?</li>

						<li>Consider what is happening right now? What is the person doing or not doing? What might they need to change or do differently?</li>

						<li>
							If there is a problem, why is it a problem? What would you want them to do differently or better? How might you be able to help them: recognise that there’s an issue and begin to do something about it? How can you support
							them?
						</li>

						<li>Keep in mind ‘the best indicator of future behaviour is past behaviour’ and changing behaviour is a long term process. Have patience and be supportive.</li>

						<li>You might choose to hold the discussion in neutral territory (not the pub!).</li>

						<li>Avoid the 'halo effect' – tendency to overrate people who had a prior good rating.</li>

						<li>Avoid the 'horns effect' - tendency to rate people lower than circumstances warrant.</li>

						<li>Avoid the 'recency error' - letting unsatisfactory work immediately prior to the review offset an entire year of performance.</li>

						<li>Avoid the 'biscuit cutter effect' - not focusing on individual specific performance and rating all employees the same.</li>

						<li>Avoid any surprises and ensure the discussions reflect your daily conversations regarding performance.</li>

						<li>Avoid giving generalised feedback about developmental areas that isn't constructive or useful.</li>
					</ul>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" flat @click="reviewerHelpDialog = false">
						Close
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<performance-review-submit-sign-off-dialog 
			:dialog="showSignOffDialog"
			@onSignOff="onSignOff"
			@onCancel="onCancelSignOff" />
	</div>
</template>

<script>
	import moment from 'moment';
	import typeFriendlyMixin from '@/mixins/typeFriendlyMixin';
	import performanceReviewItemsMixin from '@/mixins/performanceReviewItemsMixin';
	import trafficLightsMixin from '@/mixins/trafficLightsMixin';
	import avatarMixin from '@/mixins/avatarMixin';
	import { validationMixin } from 'vuelidate';
	import { required } from 'vuelidate/lib/validators';
	import TrafficLightSelector from '@/components/TrafficLightSelector';
	import TrafficLightHelper from '@/components/helpers/TrafficLightHelper';
	import PerformanceReviewSubmitSignOffDialog from '@/components/performanceReview/PerformanceReviewSubmitSignOffDialog';
	import { performanceReviewItemResultRequired, performanceReviewItemCommentRequired, performanceReviewItemOutcomeResultRequired } from '@/validators';

	let autoSaveTimeoutId;

	export default {
		components: {
			TrafficLightSelector,
			TrafficLightHelper,
			PerformanceReviewSubmitSignOffDialog
		},
		props: {
			performanceReview: {
				required: true
			},
			submit: {
				required: true
			},
			version: {
				required: true
			},
			loggedInEmployee: {
				type: Object,
				required: true
			}
		},
		data() {
			return {
				form: {
					performanceReviewId: this.performanceReview.id,
					submitId: this.submit.id,
					items: [],
					concerns: this.submit.concerns,
					otherDuties: this.submit.otherDuties,
					notes: this.submit.notes
				},
				progress: 50,
				employeeHelpDialog: false,
				reviewerHelpDialog: false,
				itemsSorted: [],
				autoSave: false,
				lastSavedDate: null,
				isSaving: false,
				isSubmitting: false,
				showSignOffDialog: false,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				}
			};
		},
		created() {
			let items = [];

			// Build form
			for (var i = 0; i < this.submit.items.length; i++) {
				let item = this.submit.items[i];
				items.push({
					id: item.id,
					item: item.item,
					outcomes: item.outcomes,
					comment: item.comment,
					result: item.result
				});
			}

			this.form.items = items;
			this.submitId = this.submit.id;

			this.itemsSorted = this.getReviewItemsSorted(this.form.items, 'item.group', 'item.category');

			this.autoSave = !this.complete && this.isMySubmit;
		},
		watch: {
			formSerialised(newVal, oldVal) {
				if(this.autoSave && oldVal != "[]") {
					// If a timer was already started, clear it.
					if (autoSaveTimeoutId) 
						clearTimeout(autoSaveTimeoutId);

					autoSaveTimeoutId = setTimeout(() => {
						if(!this.isSaving && !this.isSubmitting) {
							this.submitForm(true, false);
						}						
					}, 2000);
				}
			},
			'form.concerns'(newVal, oldVal){
				if (this.autoSave) {
					// If a timer was already started, clear it.
					if (autoSaveTimeoutId) 
						clearTimeout(autoSaveTimeoutId);

					autoSaveTimeoutId = setTimeout(() => {
						if(!this.isSaving && !this.isSubmitting) {
							this.submitForm(true, false);
						}						
					}, 2000);
				}
			 },
			 'form.otherDuties'(newVal, oldVal){
				 if (this.autoSave) {
				 	// If a timer was already started, clear it.
					if (autoSaveTimeoutId) 
						clearTimeout(autoSaveTimeoutId);

					autoSaveTimeoutId = setTimeout(() => {
						if(!this.isSaving && !this.isSubmitting) {
							this.submitForm(true, false);
						}						
					}, 2000);
				 }	
    		 },
			 'form.notes'(newVal, oldVal){
				 if (this.autoSave) {
				 	// If a timer was already started, clear it.
					if (autoSaveTimeoutId) 
						clearTimeout(autoSaveTimeoutId);

					autoSaveTimeoutId = setTimeout(() => {
						if(!this.isSaving && !this.isSubmitting) {
							this.submitForm(true, false);
						}						
					}, 2000);
				 }	
    		 },
		},
		computed: {
			formSerialised() {
				return JSON.stringify(this.itemsSorted);
			},

			isEmployee() {
				return this.version === 'Employee';
			},
			isReviewer() {
				return this.version === 'Reviewer';
			},
			isMySubmit() {
				if (this.isReviewer) 
					return this.performanceReview.reviewer.id === this.loggedInEmployee.id;
				else if (this.isEmployee)
					return this.performanceReview.employee.id === this.loggedInEmployee.id;
			},
			anyTrafficLightRankingItems() {
				return this.form.items.some(x => {
					return x.item.rankType === 1;
				});
			},
			isDefaultReviewType() {
				return this.performanceReview.type === 0;
			},
			complete() {
				return this.submit.status == 'Complete';
			},
			reviewComplete() {
				return this.performanceReview.status === 'Complete';
			},
			itemsRemainingString() {
				return `${this.itemsCompleted} / ${this.totalItems} completed`;
			},
			totalItems() {
				let count = this.form.items.length;
				for (let item of this.form.items) {
					if (item.outcomes !== null) {
						count += item.outcomes.length;
					}
				}
				return count;
			},
			itemsCompleted() {
				let count = this.form.items.filter(x => {
					if (x.item != null) {
						if (x.item.rankType === 1) {
							return x.result !== null;
						} else if (x.item.rankType === 0) {
							return x.comment !== null && x.comment !== '';
						}
					}
					return false;
				}).length;

				for (let item of this.form.items) {
					if (item.outcomes !== null) {
						count += item.outcomes.filter(y => {
							return y.result !== null;
						}).length;
					}
				}
				return count;
			},
			submitterName() {
				if (this.version == 'Employee') {
					return this.performanceReview.employee.fullName;
				} else return this.performanceReview.reviewer.fullName;
			},
			formComplete() {
				return !this.$v.form.$invalid;
			},
			saveText() {
				return this.formComplete ? 'Save & Submit' : 'Save';
			},
			saveAndSignOffText() {
				return this.complete ? 'Save & Re-Sign Off' : 'Save & Sign Off';
			},
			canEdit() {
				return this.isMySubmit && !this.reviewComplete && !this.discussionStarted;
			},
			discussionInFuture() {
				return moment.utc().isBefore(moment.utc(this.performanceReview.scheduledDateUTC));
			},
			discussionStarted() {
				return this.performanceReview.discussion.status != "Pending";
			}
		},
		mixins: [validationMixin, typeFriendlyMixin, avatarMixin, performanceReviewItemsMixin, trafficLightsMixin],

		validations: {
			form: {
				items: {
					required,
					$each: {
						comment: {
							performanceReviewItemCommentRequired
						},
						result: {
							performanceReviewItemResultRequired
						},
						outcomes: {
							$each: {
								result: {
									performanceReviewItemOutcomeResultRequired
								}
							}
						}
					}
				}
			}
		},
		methods: {
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000
			},
			getIcon(result) {
				switch (result) {
					case -1:
						return 'radio_button_unchecked';
					case 0:
						return 'clear';
					case 1:
						return 'remove';
					case 2:
						return 'check';
					default:
						return 'keyboard_arrow_right';
				}
			},
			print() {
				window.print();
			},
			onSignOffClick() {
				this.showSignOffDialog = true;
			},
			onSignOff() {
				this.submitForm(false, true)
				this.showSignOffDialog = false;
			},
			onCancelSignOff() {
				this.showSignOffDialog = false;
			},
			submitForm(viaAutoSave, signOff) {
				if (!this.isMySubmit)
					return;

				if (viaAutoSave) {
					console.log('Autosaving...');
				}
				else {
					if (signOff) {
						this.isSubmitting = true;
					}
					else {
						this.isSaving = true;
					}
				}

				let postObj = {
					isComplete: this.formComplete,
					signOff: signOff,
					performanceReviewId: this.form.performanceReviewId,
					submitId: this.form.submitId,
					concerns: this.form.concerns,
					otherDuties: this.form.otherDuties,
					notes: this.form.notes,
					items: this.form.items.map(x => {
						return {
							id: x.id,
							comment: x.comment,
							outcomes: x.outcomes.map(y => {
								return {
									id: y.id,
									result: y.result
								};
							}),
							result: x.result
						};
					})
				};

				this.$store.dispatch('performanceReviews/postSubmit', postObj).then(
					response => {
						this.isSubmitting = false;
						this.isSaving = false;

						if (signOff) {
							this.$router.push(`/pr/${response.data.id}`);
						} else {
							if (!viaAutoSave) {
								this.showSnackbar('Performance Review Saved');
							}
								
							this.lastSavedDate = new Date();
						}
					},
					error => {
						this.isSubmitting = false;
						this.isSaving = false;

						let errorMessage = 'An error has occurred whilst saving the Review: ';
						if (error) {
							if (error.response && error.response.status === 401) {
								errorMessage += 'You have been logged out. Please refresh the page to log back in.';
							} else if(error.message) {
								errorMessage += error.message;
							}
						}
						this.showSnackbar(errorMessage, true);		
					}
				);
			},
			cancel() {
				window.history.back();
			},
			onRateItem(id, result) {
				if (this.canEdit) {
					let found = this.form.items.find(x => x.id === id);
					if (!found) return;

					found.result = result;

					// If we've set the item to N/A, check to see if there are deliverables that haven't yet been ranked
					// Set them to N/A if not
					if (result === -1 && found.outcomes && found.outcomes.length > 0) {
						for (let i = 0; i < found.outcomes.length; i++) {
							if (found.outcomes[i].result === null) {
								const newVal = {...found.outcomes[i], result};
								found.outcomes.splice(i, 1, newVal);
							}
						}
					}

					this.$v.form.$touch();

					if (!this.$v.form.$invalid) {
					}
				}
			},
			onRateItemOutcome(id, outcomeId, result) {
				if (this.canEdit) {
					let foundIndex = this.form.items.findIndex(x => x.id == id);
					let foundOutcomeIndex = this.form.items[foundIndex].outcomes.findIndex(x => x.id == outcomeId);

					const newVal = {...this.form.items[foundIndex].outcomes[foundOutcomeIndex], result};
					this.form.items[foundIndex].outcomes.splice(foundOutcomeIndex, 1, newVal);

					this.$v.form.$touch();

					if (!this.$v.form.$invalid) {
					}
				}
			},
		}
	};
</script>

<style>
	button.item-btn.disabled:hover,
	.v-menu.disabled .v-menu__activator {
		cursor: default;
	}
</style>
